import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckCircleIcon, LightBulbIcon, HandRaisedIcon, ArrowPathIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../../../context/AuthContext';
import UrlConfig from '../../../data/UrlConfig';
// Importamos las dos funciones de nuestro servicio de Gemini
import { getCoreIdeaFromText, createWhyQuestionFromVision } from '../../../services/geminiService';

// --- Componente de UI: AreaSelection ---
const AreaSelection = ({ areas, selected, onSelect, onConfirm }) => (
    <div className="text-center">
        <HandRaisedIcon className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
        <h2 className="text-3xl font-bold mb-2">Elige tus 3 Áreas de Enfoque</h2>
        <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Basado en tus respuestas anteriores, estas son tus áreas con mayor oportunidad de crecimiento. Elige las 3 que más resuenen contigo para comenzar tu trabajo de introspección.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {areas.map(area => {
                const isSelected = selected.includes(area.id);
                const title = area.title.replace(/^\d+\.\s*/, '');
                return (
                    <button
                        key={area.id}
                        onClick={() => onSelect(area.id)}
                        className={`p-4 rounded-lg border-2 text-left transition-all duration-200 ${isSelected ? 'bg-purple-500/20 border-purple-500 scale-105' : 'bg-gray-800 border-gray-700 hover:border-purple-400'}`}
                    >
                        <div className="flex justify-between items-center">
                            <span className="font-bold text-lg text-white">{title}</span>
                            <span className={`w-8 h-8 flex items-center justify-center rounded-full font-black text-xl ${isSelected ? 'bg-purple-500 text-white' : 'bg-gray-700 text-gray-300'}`}>
                                {area.score}
                            </span>
                        </div>
                    </button>
                );
            })}
        </div>
        <button
            onClick={onConfirm}
            disabled={selected.length !== 3}
            className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-8 py-3 bg-purple-500 text-white font-bold rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
            Comenzar a Profundizar ({selected.length}/3)
        </button>
    </div>
);

// --- Componente de UI: AreaContext ---
const AreaContext = ({ area }) => (
    <div className="mb-6 p-4 border border-gray-700 rounded-lg bg-gray-900/50">
        <h3 className="text-2xl font-bold text-yellow-400 mb-4">{area.title.replace(/^\d+\.\s*/, '')}</h3>
        <div className="grid md:grid-cols-2 gap-4">
            <div>
                <h4 className="font-semibold text-gray-300 mb-2">Tu Realidad Actual</h4>
                <p className="text-gray-400 text-sm italic p-3 bg-gray-800 rounded-md">
                    {area.currentText || "No se encontró tu reflexión para esta área."}
                </p>
            </div>
            <div>
                <h4 className="font-semibold text-gray-300 mb-2">Tu Visión Futura (El Mejor Año)</h4>
                <p className="text-gray-400 text-sm italic p-3 bg-gray-800 rounded-md">
                    {area.futureText || "No se encontró tu visión para esta área."}
                </p>
            </div>
        </div>
    </div>
);

// --- Componente de UI: ActivitySummary ---
const ActivitySummary = ({ nextActivity, finalWhys, areas, onRedo, onProceed }) => (
    <div className="bg-gray-900/50 p-8 rounded-xl border border-yellow-500/30 text-center animate-fade-in">
        <CheckCircleIcon className="w-16 h-16 text-green-400 mx-auto mb-4" />
        <h2 className="text-3xl font-bold mb-4">¡Has Encontrado tus Motores!</h2>
        <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Este es un logro monumental. Has descubierto las verdaderas raíces que te impulsan a querer un cambio en tus áreas más importantes.
        </p>
        <div className="space-y-6 text-left max-w-3xl mx-auto">
            {areas.map(area => (
                <div key={area.id} className="bg-gray-800 p-5 rounded-lg">
                    <h3 className="font-bold text-yellow-400 text-xl">{area.title.replace(/^\d+\.\s*/, '')}</h3>
                    <div className="flex items-start gap-3 mt-3">
                        <LightBulbIcon className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1"/>
                        <p className="text-lg text-purple-200 italic">"{finalWhys[area.id] || 'No se completó el porqué final para esta área.'}"</p>
                    </div>
                </div>
            ))}
        </div>
        <p className="mt-8 text-gray-400">Guarda estos "porqués" en un lugar visible. Serán tu combustible inagotable.</p>
        <div className="mt-10 flex flex-col sm:flex-row justify-center items-center gap-4">
            <button onClick={onRedo} className="flex items-center justify-center gap-2 w-full sm:w-auto bg-gray-700 text-gray-300 font-bold py-3 px-8 rounded-lg hover:bg-gray-600 transition-all duration-300">
                <ArrowPathIcon className="w-5 h-5"/>
                Rehacer Ejercicio
            </button>
            {nextActivity && (
                <button onClick={onProceed} className="w-full sm:w-auto inline-flex items-center gap-2 bg-yellow-500 text-black font-bold py-3 px-8 rounded-lg hover:bg-yellow-400 transition-colors">
                    Siguiente Actividad
                    <ArrowRightIcon className="w-5 h-5"/>
                </button>
            )}
        </div>
         <div className="text-center mt-6">
             <Link to="/cursos/manifiesta" className="text-sm text-gray-500 hover:text-yellow-400 transition-colors">Volver al Temario</Link>
        </div>
    </div>
);


// --- Componente Principal: SevenLevelsDeepWhyActivity ---
const SevenLevelsDeepWhyActivity = ({ nextActivity, onProceed }) => {
    const { user, getGuestActivityData, saveGuestActivityProgress } = useAuth();
    const activityId = '7-niveles-deep-why';

    // Estados del componente
    const [phase, setPhase] = useState('loading');
    const [error, setError] = useState(null);
    const [allLowestAreas, setAllLowestAreas] = useState([]);
    const [selectedAreaIds, setSelectedAreaIds] = useState([]);
    const [targetAreas, setTargetAreas] = useState([]);
    const [whyAnswers, setWhyAnswers] = useState({});
    const [currentAreaIndex, setCurrentAreaIndex] = useState(0);
    const [currentLevelIndex, setCurrentLevelIndex] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const textAreaRef = useRef(null);
    const [currentPrompt, setCurrentPrompt] = useState('');
    const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
    
    const fullAreaDetails = useMemo(() => ({
        'love-relationship': "Tu Relación Amorosa", 'friendships': "Tus Amistades", 'finances': "Tus Finanzas", 'quality-of-life': "Tu Calidad de Vida", 'health-fitness': "Tu Salud y Estado Físico", 'intellectual-life': "Tu Vida Intelectual", 'emotional-life': "Tu Vida Emocional", 'spiritual-life': "Tu Vida Espiritual", 'career': "Tu Carrera", 'character': "Tu Carácter", 'family-parenting': "Tu Familia y Paternidad", 'life-vision': "Tu Visión de Vida"
    }), []);

    // Efecto para cargar los datos iniciales de la actividad
    useEffect(() => {
        const loadData = async () => {
            setError(null); setPhase('loading');
            try {
                let prerequisitesData;
                if (user) {
                    const response = await fetch(UrlConfig.activity_getDeepWhyPrerequisites, { credentials: 'include' });
                    if (!response.ok) { const errData = await response.json(); throw new Error(errData.message || 'Error del servidor.'); }
                    const result = await response.json();
                    if (!result.success) throw new Error(result.message);
                    prerequisitesData = result.data; // Aquí esperamos { targetAreas, whyAnswers, selectedAreaIds, isCompleted }
                } else {
                    const activity1Data = getGuestActivityData('12-areas-balance');
                    if (!activity1Data || Object.keys(activity1Data).length === 0) throw new Error("Debes completar 'Rueda de la Vida' primero.");
                    const activity2Data = getGuestActivityData('concientizando-areas-balance');
                    const activity3Data = getGuestActivityData('viendo-hacia-atras-desde-el-futuro');
                    const guestWhyData = getGuestActivityData(activityId);
                    const sortedAreas = Object.entries(activity1Data).map(([id, score]) => ({ id, score })).sort((a, b) => a.score - b.score);
                    const targetAreasFromGuest = sortedAreas.slice(0, 7).map(area => ({ ...area, title: fullAreaDetails[area.id] || area.id, currentText: activity2Data?.[area.id], futureText: activity3Data?.[area.id] }));
                    prerequisitesData = {
                        targetAreas: targetAreasFromGuest,
                        whyAnswers: guestWhyData?.whyAnswers || {},
                        selectedAreaIds: guestWhyData?.selectedAreaIds || [],
                        isCompleted: guestWhyData?.isCompleted || false // Leer la bandera de completado para el invitado
                    };
                }

                // ================== INICIO DE LA LÓGICA DE CARGA MODIFICADA ==================
                setAllLowestAreas(prerequisitesData.targetAreas || []);
                const initialAnswers = prerequisitesData.whyAnswers || {};
                setWhyAnswers(initialAnswers);
                const initialSelection = prerequisitesData.selectedAreaIds || [];
                const isAlreadyCompleted = prerequisitesData.isCompleted || false;
                
                // 1. Prioridad #1: Si la actividad ya fue completada, ir directo al resumen.
                if (isAlreadyCompleted) {
                    setSelectedAreaIds(initialSelection);
                    const chosenAreas = (prerequisitesData.targetAreas || []).filter(area => initialSelection.includes(area.id));
                    setTargetAreas(chosenAreas);
                    setPhase('summary');
                
                // 2. Si no está completada pero ya hay áreas seleccionadas, continuar la actividad.
                } else if (initialSelection.length > 0) {
                    setSelectedAreaIds(initialSelection);
                    const chosenAreas = (prerequisitesData.targetAreas || []).filter(area => initialSelection.includes(area.id));
                    setTargetAreas(chosenAreas);
                    setPhase('activity');
                
                // 3. Si no hay nada, empezar desde la selección de áreas.
                } else {
                    setPhase('selection');
                }
                // =================== FIN DE LA LÓGICA DE CARGA MODIFICADA ===================

            } catch (err) { setError(err.message); setPhase('error'); }
        };
        loadData();
    }, [user, getGuestActivityData, activityId, fullAreaDetails]);

    const currentArea = targetAreas[currentAreaIndex];

    // Efecto para generar la pregunta dinámicamente usando Gemini
    useEffect(() => {
        if (phase === 'activity' && currentArea) {
            const generatePrompt = async () => {
                setIsGeneratingPrompt(true);
                setCurrentPrompt('');
                
                if (currentLevelIndex === 0) {
                    const visionText = currentArea.futureText;
                    const currentText = currentArea.currentText;
                    if (visionText) {
                        const firstPrompt = await createWhyQuestionFromVision(currentText, visionText);
                        setCurrentPrompt(firstPrompt);
                    } else {
                        setCurrentPrompt(`Con base en tu realidad actual y tu visión futura, ¿por qué es importante para ti mejorar en "${currentArea.title.replace(/^\d+\.\s*/, '')}"?`);
                    }
                } else {
                    const allAnswersForArea = whyAnswers[currentArea.id] || [];
                    const previousAnswer = allAnswersForArea[currentLevelIndex - 1] || '...';
                    const answerHistory = allAnswersForArea.slice(0, currentLevelIndex - 1);
                    
                    const coreIdea = await getCoreIdeaFromText(previousAnswer, answerHistory);
                    const newPrompt = `Entendido. ¿Y por qué es importante para ti "${coreIdea}"?`;
                    setCurrentPrompt(newPrompt);
                }
                setIsGeneratingPrompt(false);
            };

            generatePrompt();
        }
    }, [phase, currentArea, currentLevelIndex]); // No incluir 'whyAnswers' para evitar re-generaciones innecesarias

    // Efecto para enfocar el textarea una vez que la pregunta se ha generado
    useEffect(() => {
        if (phase === 'activity' && textAreaRef.current && !isGeneratingPrompt) {
            textAreaRef.current.focus();
        }
    }, [phase, currentPrompt, isGeneratingPrompt]);


    // --- Funciones de manejo de eventos ---
    const handleConfirmSelection = async () => {
        if (selectedAreaIds.length !== 3) return;
        const chosenAreas = allLowestAreas.filter(area => selectedAreaIds.includes(area.id));
        setTargetAreas(chosenAreas);
        if (user) {
            setIsSaving(true);
            try {
                await fetch(UrlConfig.activity_saveWhySelection, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ activityId, selectedAreaIds }) });
            } catch (err) { setError("No se pudo guardar la selección."); } finally { setIsSaving(false); }
        } else { saveGuestActivityProgress(activityId, { selectedAreaIds, whyAnswers }); }
        setPhase('activity');
    };
    
    const handleRedoActivity = () => {
        // NOTA: Reiniciar no debería borrar el progreso guardado, solo el estado local.
        setPhase('selection'); setSelectedAreaIds([]); setTargetAreas([]); setCurrentAreaIndex(0); setCurrentLevelIndex(0);
    };
    
    const handleAnswerChange = (e) => {
        const newAnswers = { ...whyAnswers };
        if (!newAnswers[currentArea.id]) newAnswers[currentArea.id] = [];
        newAnswers[currentArea.id][currentLevelIndex] = e.target.value;
        setWhyAnswers(newAnswers);
    };

    const goToNext = async () => {
        const answer = whyAnswers[currentArea.id]?.[currentLevelIndex] || '';
        if (answer.trim() === '') { setError('Por favor, escribe una respuesta.'); return; }
        setError(null);
        if (user) {
            setIsSaving(true);
            try {
                await fetch(UrlConfig.activity_saveWhyAnswer, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ activityId, areaId: currentArea.id, levelIndex: currentLevelIndex, answer }) });
            } catch (err) { setError(err.message); } finally { setIsSaving(false); }
        } else { saveGuestActivityProgress(activityId, { whyAnswers, selectedAreaIds }); }
        
        const isLastLevel = currentLevelIndex === 6;
        const isLastArea = currentAreaIndex === targetAreas.length - 1;

        if (isLastLevel && isLastArea) {
            if (user) {
                 await fetch(UrlConfig.activity_markAsComplete, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ activityId }) });
            } else {
                // MODIFICADO: Guardar el estado de completado para el invitado
                saveGuestActivityProgress(activityId, { whyAnswers, selectedAreaIds, isCompleted: true });
            }
            setPhase('summary');
        } else if (isLastLevel) {
            setCurrentAreaIndex(prev => prev + 1); setCurrentLevelIndex(0);
        } else { setCurrentLevelIndex(prev => prev + 1); }
    };
    
    const handleProceedToSummary = async () => {
        if (user) {
            await fetch(UrlConfig.activity_markAsComplete, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                body: JSON.stringify({ activityId })
            });
        } else {
            // MODIFICADO: Guardar el estado de completado para el invitado en finalización temprana
            saveGuestActivityProgress(activityId, { whyAnswers, selectedAreaIds, isCompleted: true });
        }
        setPhase('summary');
    };

    // --- Lógica de Renderizado ---
    if (phase === 'loading') return <div className="flex justify-center items-center h-40"><p className="text-center text-yellow-400 animate-pulse text-lg">Cargando tus áreas de enfoque...</p></div>;
    if (phase === 'error') return <div className="text-center p-8 bg-red-900/50 border border-red-500 rounded-lg"><p className="text-red-400 font-bold">Error al Cargar</p><p className="text-red-300 mt-2">{error}</p></div>;
    if (phase === 'selection') return <AreaSelection areas={allLowestAreas} selected={selectedAreaIds} onSelect={(id) => setSelectedAreaIds(p => p.includes(id) ? p.filter(i => i !== id) : (p.length < 3 ? [...p, id] : p))} onConfirm={handleConfirmSelection} />;
    
    if (phase === 'summary') {
        const finalWhys = targetAreas.reduce((acc, area) => {
            acc[area.id] = whyAnswers[area.id]?.[6] || "No se completó el porqué final.";
            return acc;
        }, {});
        return <ActivitySummary nextActivity={nextActivity} finalWhys={finalWhys} areas={targetAreas} onRedo={handleRedoActivity} onProceed={onProceed} />;
    }
    
    if (phase === 'activity' && currentArea) {
        const progressPercentage = ((currentAreaIndex * 7 + currentLevelIndex) / (targetAreas.length * 7)) * 100;
        return (
            <div className="animate-fade-in">
                <div className="mb-6">
                    <p className="text-center text-purple-300 text-sm mb-2">Progreso Total de la Actividad</p>
                    <div className="w-full bg-gray-700 rounded-full h-2.5"><div className="bg-purple-500 h-2.5 rounded-full transition-all duration-500" style={{ width: `${progressPercentage}%` }}></div></div>
                </div>
                <div className="mb-4 text-center">
                    <p className="text-yellow-400 font-bold text-sm tracking-wider">ÁREA DE ENFOQUE {currentAreaIndex + 1} DE {targetAreas.length}</p>
                    <p className="text-purple-400 font-bold text-sm tracking-wider mt-1">NIVEL DE PROFUNDIDAD {currentLevelIndex + 1} DE 7</p>
                </div>
                <AreaContext area={currentArea} />
                <div className="mt-8 bg-gray-900 p-6 rounded-lg border border-purple-500/30">
                    {isGeneratingPrompt ? (
                        <p className="block text-xl font-semibold text-white mb-4 italic animate-pulse">Generando pregunta...</p>
                    ) : (
                        <label className="block text-xl font-semibold text-white mb-4 italic" htmlFor="why-answer">
                            {currentPrompt}
                        </label>
                    )}
                    <textarea 
                        id="why-answer" 
                        ref={textAreaRef} 
                        className="w-full h-40 p-4 bg-gray-800 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition" 
                        placeholder="Escribe desde el corazón, no desde la cabeza..." 
                        value={whyAnswers[currentArea.id]?.[currentLevelIndex] || ''} 
                        onChange={handleAnswerChange} 
                        onKeyDown={(e) => {if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {goToNext()}}}
                        disabled={isGeneratingPrompt}
                    />
                </div>
                {error && <p className="text-red-500 mt-2 text-sm text-center">{error}</p>}
                <div className="mt-8 flex justify-end items-center">
                    <span className="text-xs text-gray-500 mr-4 hidden md:block">o presiona <kbd className="px-2 py-1.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-lg">Ctrl</kbd> + <kbd className="px-2 py-1.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-lg">Enter</kbd> para avanzar</span>
                    <button 
                        onClick={goToNext} 
                        disabled={isSaving || isGeneratingPrompt} 
                        className="flex items-center gap-2 px-6 py-3 bg-purple-500 text-white font-bold rounded-lg hover:bg-purple-600 disabled:opacity-50 transition-colors"
                    >
                        {isSaving ? 'Guardando...' : 'Siguiente Nivel'}
                        {!isSaving && <ArrowRightIcon className="w-5 h-5" />}
                    </button>
                </div>
                
                {currentAreaIndex >= 1 && phase === 'activity' && (
                    <div className="text-center mt-6">
                        <p className="text-gray-400 text-sm mb-2">
                            Puedes continuar con la siguiente área o finalizar el ejercicio aquí.
                        </p>
                        <button
                            onClick={handleProceedToSummary}
                            disabled={isSaving}
                            className="text-yellow-500 hover:text-yellow-400 font-bold underline transition-colors disabled:opacity-50"
                        >
                            Finalizar ahora y ver el resumen
                        </button>
                    </div>
                )}
            </div>
        );
    }

    return <div className="flex justify-center items-center h-40"><p className="text-center text-gray-400">Cargando actividad...</p></div>;
};

export default SevenLevelsDeepWhyActivity;